/**
 * Performance review sheet — goals, live metrics, self vs manager ratings,
 * score preview, manager submission and admin approval.
 */
import { StaffRatingCard } from '../../../components/admin/feedback/RatingBits';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, Trash2, Save, FileDown, CheckCircle2, Undo2, Mail,
  Activity, Target, MessageSquare, ShieldCheck, LifeBuoy, Grid3x3, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import {
  BAND_STYLE, STATUS_STYLE, statusLabel, Badge, ScoreRing, ProgressBar,
  RatingPicker, fmtMetric,
} from './shared';
import { LevelChip } from '../skills/shared';

const ReviewDetailPage = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const { authHeader } = useAuth();

  const [meta, setMeta] = useState(null);
  const [review, setReview] = useState(null);
  const [goals, setGoals] = useState([]);
  const [mgr, setMgr] = useState({
    competencies: {}, goal_actuals: {}, goal_notes: {}, customer_rating: '',
    overall_comment: '', strengths: '', improvements: '', recommendation: 'increment',
  });
  const [admin, setAdmin] = useState({ admin_comment: '', increment_pct: '', increment_effective_from: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data }] = await Promise.all([
        axios.get(`${API}/performance/meta`, { headers: authHeader() }),
        axios.get(`${API}/performance/reviews/${rid}`, { headers: authHeader() }),
      ]);
      setMeta(m);
      setReview(data);
      setGoals((data.goals || []).map((g) => ({ ...g })));
      setMgr({
        competencies: { ...(data.manager_competencies || {}) },
        goal_actuals: Object.fromEntries((data.goals || []).map((g) => [g.id, g.actual ?? ''])),
        goal_notes: Object.fromEntries((data.goals || []).map((g) => [g.id, g.manager_note || ''])),
        customer_rating: data.customer_rating ?? '',
        overall_comment: data.manager_overall_comment || '',
        strengths: data.manager_strengths || '',
        improvements: data.manager_improvements || '',
        recommendation: data.manager_recommendation || 'increment',
      });
      setAdmin({
        admin_comment: data.admin_comment || '',
        increment_pct: data.increment_pct ?? '',
        increment_effective_from: data.salary_revision?.effective_from
          || m.default_increment_effective_from || '',
      });
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, rid]);

  useEffect(() => { load(); }, [load]);

  const [sopStatus, setSopStatus] = useState([]);
  const staffId = review?.staff_id;
  useEffect(() => {
    if (!staffId) return;
    axios.get(`${API}/sop/staff/${staffId}/acknowledgements`, { headers: authHeader() })
      .then(({ data }) => setSopStatus(data.flows || []))
      .catch(() => setSopStatus([]));
  }, [authHeader, staffId]);

  const canEdit = review && ['hr', 'manager'].includes(review.viewer_role) && review.status !== 'published';
  const isHr = review?.viewer_role === 'hr';
  const weightTotal = useMemo(
    () => goals.reduce((sum, g) => sum + Number(g.weight || 0), 0),
    [goals],
  );

  const metricDef = (key) => (meta?.metrics || []).find((m) => m.key === key) || {};

  const addGoal = () => setGoals((gs) => [...gs, {
    id: `new_${Date.now()}`, title: '', metric_key: 'manual', target: 0, weight: 0,
    unit: '', description: '',
  }]);

  const saveGoals = async () => {
    setBusy('goals');
    try {
      const payload = {
        goals: goals.map((g) => ({
          id: String(g.id).startsWith('new_') ? null : g.id,
          title: g.title, metric_key: g.metric_key,
          target: Number(g.target || 0), weight: Number(g.weight || 0),
          unit: g.unit || metricDef(g.metric_key).unit || '',
          direction: g.direction || metricDef(g.metric_key).direction || 'higher',
          description: g.description || '',
        })),
      };
      await axios.put(`${API}/performance/reviews/${rid}/goals`, payload, { headers: authHeader() });
      toast.success('Goal sheet saved');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const submitManager = async () => {
    setBusy('manager');
    try {
      await axios.post(`${API}/performance/reviews/${rid}/manager`, {
        competencies: mgr.competencies,
        goal_actuals: Object.fromEntries(Object.entries(mgr.goal_actuals).map(
          ([k, v]) => [k, v === '' || v === null ? null : Number(v)],
        )),
        goal_notes: mgr.goal_notes,
        customer_rating: mgr.customer_rating === '' ? null : Number(mgr.customer_rating),
        overall_comment: mgr.overall_comment,
        strengths: mgr.strengths,
        improvements: mgr.improvements,
        recommendation: mgr.recommendation,
      }, { headers: authHeader() });
      toast.success('Evaluation submitted for management approval');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const approve = async (publish) => {
    setBusy('approve');
    try {
      await axios.post(`${API}/performance/reviews/${rid}/approve`, {
        admin_comment: admin.admin_comment,
        increment_pct: admin.increment_pct === '' ? null : Number(admin.increment_pct),
        increment_effective_from: admin.increment_effective_from || null,
        publish,
      }, { headers: authHeader() });
      toast.success(publish ? 'Appraisal approved & published to the staff member' : 'Appraisal approved');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const reopen = async () => {
    setBusy('reopen');
    try {
      await axios.post(`${API}/performance/reviews/${rid}/reopen`, {}, { headers: authHeader() });
      toast.success('Review reopened for edits');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const startPip = async () => {
    setBusy('pip');
    try {
      const { data } = await axios.post(`${API}/performance/pips`, {
        staff_id: review.staff_id,
        review_id: rid,
        status: 'open',
        objectives: [
          'Meet the agreed job completion target every month',
          'Deliver every job on or before the promised date',
          'Maintain attendance above 95% with no unplanned absence',
          'Zero customer complaints on workmanship',
        ],
      }, { headers: authHeader() });
      toast.success(`90-day plan started (${data.start_date} → ${data.end_date})`);
      navigate('/admin/performance/plans');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const emailAppraisal = async () => {
    setBusy('email');
    try {
      const { data } = await axios.post(`${API}/performance/reviews/${rid}/email`, {},
        { headers: authHeader() });
      toast.success(`Appraisal emailed to ${data.to}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const res = await axios.get(`${API}/performance/reviews/${rid}/pdf`, {
        headers: authHeader(), responseType: 'blob', timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = `Appraisal_${review?.staff_name || 'staff'}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  }
  if (err || !review) {
    return (
      <div className="space-y-3">
        <button onClick={() => navigate('/admin/performance')} className="text-sm text-slate-600 flex items-center gap-1"><ArrowLeft size={15} /> Back</button>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err || 'Review not found'}</div>
      </div>
    );
  }

  const m = review.auto_metrics || {};
  const scored = review.goals_scored || [];

  return (
    <div className="space-y-5 pb-24" data-testid="review-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => navigate('/admin/performance')} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1" data-testid="back-btn">
            <ArrowLeft size={14} /> Team scorecard
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">{review.staff_name}</h1>
          <p className="text-sm text-slate-500">
            {review.staff_role || '—'} · {review.cycle_name} ({review.period_start} → {review.period_end})
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Badge className={STATUS_STYLE[review.status] || 'bg-slate-100 text-slate-600'} testid="review-status">
              {statusLabel(review.status)}
            </Badge>
            <Badge className="bg-slate-100 text-slate-600">Reports to {review.reporting_manager_name || 'unassigned'}</Badge>
            {review.self_submitted_at && <Badge className="bg-sky-100 text-sky-700">Self review in</Badge>}
            {review.band_override && (
              <Badge className="bg-amber-100 text-amber-800" testid="band-override-badge">
                Band calibrated {review.computed_band} → {review.band}
              </Badge>
            )}
            {review.salary_revision && review.salary_revision.status === 'pending' && (
              <Badge className="bg-emerald-100 text-emerald-800" testid="revision-badge">
                +{review.salary_revision.increment_pct}% from {review.salary_revision.effective_from}
              </Badge>
            )}
          </div>
          {['hr', 'manager'].includes(review.viewer_role) && (
            <button
              onClick={startPip} disabled={busy === 'pip'}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 text-xs font-medium"
              data-testid="start-pip-btn"
            >
              {busy === 'pip' ? <Loader2 size={13} className="animate-spin" /> : <LifeBuoy size={13} />}
              Start 90-day improvement plan
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <ScoreRing value={review.final_score} band={review.band} />
          <div className="text-xs">
            <div className="text-slate-500">Final score</div>
            <div className="text-lg font-semibold text-slate-900" data-testid="final-score">{review.final_score}/100</div>
            <Badge className={BAND_STYLE[review.band]}>{review.band} · {review.band_label}</Badge>
            <div className="text-slate-500 mt-1">
              Goals {review.goal_score}% · Behaviour {review.competency_score}%
              {review.skill_score !== null && review.skill_score !== undefined && (
                <> · Skills {review.skill_score}%</>
              )}
            </div>
          </div>
          <div className="self-start flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-1.5">
          {review.status === 'published' && review.viewer_role !== 'owner' && (
            <button
              onClick={emailAppraisal} disabled={busy === 'email'}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-teal-300 bg-teal-50 text-xs text-teal-800 hover:bg-teal-100 disabled:opacity-50"
              title="Email this appraisal to the employee with the PDF attached"
              data-testid="email-appraisal-btn"
            >
              {busy === 'email' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {review.emailed_at ? 'Email again' : 'Email to employee'}
            </button>
          )}
          <button
            onClick={downloadPdf} disabled={busy === 'pdf'}
            className="self-start inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="download-pdf-btn"
          >
            {busy === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            PDF
          </button>
          </div>
          {review.emailed_at && (
            <span className="text-[11px] text-slate-500" data-testid="emailed-note">
              Sent to {review.emailed_to} on {String(review.emailed_at).replace('T', ' ').slice(0, 16)}
            </span>
          )}
          </div>
        </div>
      </div>

      <StaffRatingCard staffId={review.staff_id} />

      {/* Work responsibilities — what this person is actually accountable for */}
      {(() => {
        const st = review.staff || {};
        const duties = st.responsibilities || [];
        if (!st.role_summary && duties.length === 0 && (sopStatus || []).length === 0) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="review-responsibilities">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2">
              <ClipboardList size={15} className="text-teal-600" /> Work responsibilities
              <span className="font-normal text-xs text-slate-400">— judge performance against these</span>
            </h2>
            {st.role_summary && (
              <p className="text-sm font-medium text-slate-900" data-testid="review-role-summary">{st.role_summary}</p>
            )}
            {duties.length > 0 && (
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {duties.map((d, i) => (
                  <li key={`${i}-${d}`} className="flex gap-2 text-sm text-slate-700" data-testid={`review-responsibility-${i}`}>
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to={`/admin/skills/staff/${review.staff_id}`} className="mt-2.5 inline-block text-xs text-teal-700 underline" data-testid="edit-responsibilities-link">
              Update on the skill sheet →
            </Link>
            {(sopStatus || []).length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                  SOP confirmations
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="review-sop-status">
                  {sopStatus.map((f) => (
                    <span
                      key={f.flow_id}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                        f.read_at ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                      data-testid={`sop-chip-${f.flow_id}`}
                    >
                      {f.read_at ? <CheckCircle2 size={11} /> : <MessageSquare size={11} />}
                      {f.title}: {f.read_at ? `read ${f.read_at.slice(0, 10)}` : 'not confirmed'}
                      {f.watched_at ? ' · watched' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Live metrics */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
          <Activity size={15} className="text-teal-600" /> System-tracked activity
          <span className="text-xs font-normal text-slate-400">as of {m.as_of || '—'}</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="auto-metrics">
          {[
            { label: 'Jobs completed', value: m.jobs_completed ?? 0 },
            { label: 'On-time delivery', value: `${m.on_time_pct ?? 0}%` },
            { label: 'Inspections', value: m.inspections_done ?? 0 },
            { label: 'Attendance', value: `${m.attendance_pct ?? 0}%` },
            { label: 'Days present', value: `${m.days_present ?? 0}/${m.working_days ?? 0}` },
            { label: 'OT hours', value: m.ot_hours ?? 0 },
            { label: 'Teammates lifted', value: m.mentoring_credits ?? 0 },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
              <div className="text-base font-semibold text-slate-900">{k.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Target size={15} className="text-teal-600" /> Goals & KRAs
          </h2>
          {canEdit && (
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${Math.abs(weightTotal - 100) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="weight-total">
                Weights: {weightTotal}% / 100%
              </span>
              <button onClick={addGoal} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-700 hover:bg-slate-50" data-testid="add-goal-btn">
                <Plus size={13} /> Goal
              </button>
              <button onClick={saveGoals} disabled={busy === 'goals'} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs hover:bg-slate-800 disabled:opacity-50" data-testid="save-goals-btn">
                {busy === 'goals' ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save goals
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {(canEdit ? goals : scored).map((g, idx) => {
            const live = scored.find((x) => x.id === g.id) || {};
            const def = metricDef(g.metric_key);
            return (
              <div key={g.id} className="rounded-lg border border-slate-200 p-3" data-testid={`goal-${idx}`}>
                {canEdit ? (
                  <div className="grid gap-2 sm:grid-cols-12">
                    <input
                      className={`${inputCls} sm:col-span-4`} placeholder="Goal title"
                      value={g.title}
                      onChange={(e) => setGoals((gs) => gs.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))}
                      data-testid={`goal-title-${idx}`}
                    />
                    <select
                      className={`${inputCls} sm:col-span-3`} value={g.metric_key}
                      onChange={(e) => setGoals((gs) => gs.map((x, i) => i === idx
                        ? { ...x, metric_key: e.target.value, unit: metricDef(e.target.value).unit || '' } : x))}
                      data-testid={`goal-metric-${idx}`}
                    >
                      {(meta?.metrics || []).map((mm) => <option key={mm.key} value={mm.key}>{mm.label}</option>)}
                    </select>
                    <input
                      type="number" step="0.01" className={`${inputCls} sm:col-span-2`} placeholder="Target"
                      value={g.target}
                      onChange={(e) => setGoals((gs) => gs.map((x, i) => i === idx ? { ...x, target: e.target.value } : x))}
                      data-testid={`goal-target-${idx}`}
                    />
                    <input
                      type="number" step="1" className={`${inputCls} sm:col-span-2`} placeholder="Weight %"
                      value={g.weight}
                      onChange={(e) => setGoals((gs) => gs.map((x, i) => i === idx ? { ...x, weight: e.target.value } : x))}
                      data-testid={`goal-weight-${idx}`}
                    />
                    <button
                      onClick={() => setGoals((gs) => gs.filter((_, i) => i !== idx))}
                      className="sm:col-span-1 p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 justify-self-start"
                      data-testid={`remove-goal-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                    {def.help && <p className="sm:col-span-12 text-[11px] text-slate-400">{def.help}</p>}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">{g.title}</div>
                      <div className="text-xs text-slate-500">
                        {def.label || g.metric_key} · weight {g.weight}% · target {fmtMetric(g.target, g.unit)}
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="text-slate-900 font-mono">{fmtMetric(live.actual_resolved ?? g.actual_resolved, g.unit)}</div>
                      <div className="text-slate-500">{live.achieved_pct ?? g.achieved_pct ?? '—'}% achieved</div>
                    </div>
                  </div>
                )}

                <div className="mt-2">
                  <ProgressBar pct={live.achieved_pct ?? g.achieved_pct} />
                </div>

                {canEdit && (
                  <div className="grid gap-2 sm:grid-cols-12 mt-2">
                    <input
                      type="number" step="0.01" className={`${inputCls} sm:col-span-3`}
                      placeholder={`Actual (auto: ${fmtMetric(live.actual_resolved, g.unit)})`}
                      value={mgr.goal_actuals[g.id] ?? ''}
                      onChange={(e) => setMgr((s) => ({ ...s, goal_actuals: { ...s.goal_actuals, [g.id]: e.target.value } }))}
                      data-testid={`goal-actual-${idx}`}
                    />
                    <input
                      className={`${inputCls} sm:col-span-9`} placeholder="Manager note on this goal"
                      value={mgr.goal_notes[g.id] ?? ''}
                      onChange={(e) => setMgr((s) => ({ ...s, goal_notes: { ...s.goal_notes, [g.id]: e.target.value } }))}
                      data-testid={`goal-note-${idx}`}
                    />
                  </div>
                )}
                {g.self_note && (
                  <p className="mt-2 text-xs text-slate-600 bg-sky-50 border border-sky-100 rounded p-2">
                    <span className="text-sky-700 font-medium">Self:</span> {g.self_note}
                  </p>
                )}
              </div>
            );
          })}
          {!goals.length && !scored.length && (
            <p className="text-sm text-slate-500" data-testid="no-goals">No goals set yet.</p>
          )}
        </div>
      </div>

      {/* Skill mapping */}
      {review.skill_detail?.rows?.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="skill-block">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Grid3x3 size={15} className="text-teal-600" /> Skill mapping
              <span className="text-xs font-normal text-slate-400">
                assessed {review.skill_detail.assessment_date}
              </span>
            </h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">
                Index <span className="font-mono font-semibold text-slate-900">{review.skill_detail.skill_index}%</span>
                {review.skill_detail.delta !== null && review.skill_detail.delta !== undefined && (
                  <span className={review.skill_detail.delta >= 0 ? 'text-emerald-600 ml-1' : 'text-red-600 ml-1'}>
                    ({review.skill_detail.delta >= 0 ? '+' : ''}{review.skill_detail.delta} vs previous)
                  </span>
                )}
              </span>
              <Link
                to={`/admin/skills/staff/${review.staff_id}`}
                className="text-teal-700 hover:text-teal-900 font-medium"
                data-testid="open-skill-sheet"
              >
                Open skill sheet →
              </Link>
            </div>
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {review.skill_detail.rows.map((r) => (
              <div key={r.name} className="flex items-center justify-between gap-2 border-b border-slate-100 pb-1">
                <span className="text-xs text-slate-700 truncate">
                  <span className="text-slate-400">{r.group} · </span>{r.name}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {r.prev_level && <LevelChip level={r.prev_level} size="sm" />}
                  <LevelChip level={r.level} size="sm" />
                </span>
              </div>
            ))}
          </div>
          {review.skill_detail.training_needs && (
            <p className="text-xs text-slate-600 mt-3">
              <span className="text-slate-500">Training needs: </span>{review.skill_detail.training_needs}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="no-skill-block">
          No signed-off skill assessment on file — skills carry 30% of the score, so goals and
          behaviour are re-weighted until a supervisor marks one as checked.{' '}
          <Link to={`/admin/skills/staff/${review.staff_id}`} className="font-semibold underline">
            Record a skill assessment
          </Link>
        </div>
      )}

      {/* Competencies */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
          <MessageSquare size={15} className="text-teal-600" /> Competency assessment
        </h2>
        <div className="space-y-2">
          {(meta?.competencies || []).map((c) => (
            <div key={c.key} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
              <div className="text-sm text-slate-800">{c.label}</div>
              <div className="flex items-center gap-4">
                <div className="text-xs text-slate-500">
                  Self: <span className="font-mono text-slate-800">{review.self_competencies?.[c.key] ?? '—'}</span>
                </div>
                <RatingPicker
                  value={mgr.competencies[c.key]}
                  disabled={!canEdit}
                  onChange={(n) => setMgr((s) => ({ ...s, competencies: { ...s.competencies, [c.key]: n } }))}
                  testid={`comp-${c.key}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-3 mt-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Customer rating (0–5)</label>
            <input
              type="number" step="0.1" min="0" max="5" className={inputCls} disabled={!canEdit}
              value={mgr.customer_rating}
              onChange={(e) => setMgr((s) => ({ ...s, customer_rating: e.target.value }))}
              data-testid="customer-rating-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Recommendation</label>
            <select
              className={inputCls} disabled={!canEdit} value={mgr.recommendation}
              onChange={(e) => setMgr((s) => ({ ...s, recommendation: e.target.value }))}
              data-testid="recommendation-select"
            >
              {(meta?.recommendations || []).map((r) => <option key={r} value={r}>{statusLabel(r)}</option>)}
            </select>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Strengths</label>
            <textarea rows={3} className={inputCls} disabled={!canEdit} value={mgr.strengths}
              onChange={(e) => setMgr((s) => ({ ...s, strengths: e.target.value }))} data-testid="strengths-input" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Areas to improve</label>
            <textarea rows={3} className={inputCls} disabled={!canEdit} value={mgr.improvements}
              onChange={(e) => setMgr((s) => ({ ...s, improvements: e.target.value }))} data-testid="improvements-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Overall manager comment</label>
            <textarea rows={3} className={inputCls} disabled={!canEdit} value={mgr.overall_comment}
              onChange={(e) => setMgr((s) => ({ ...s, overall_comment: e.target.value }))} data-testid="manager-comment-input" />
          </div>
        </div>

        {review.self_overall_comment && (
          <div className="mt-3 text-xs text-slate-700 bg-sky-50 border border-sky-100 rounded p-3" data-testid="self-comment">
            <span className="text-sky-700 font-medium">Employee self assessment:</span> {review.self_overall_comment}
          </div>
        )}

        {canEdit && (
          <button
            onClick={submitManager} disabled={busy === 'manager'}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
            data-testid="submit-manager-btn"
          >
            {busy === 'manager' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            Submit evaluation
          </button>
        )}
      </div>

      {/* Admin approval */}
      {isHr && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="approval-panel">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
            <ShieldCheck size={15} className="text-teal-600" /> Management approval
          </h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Approved increment %</label>
              <input
                type="number" step="0.1" className={inputCls} value={admin.increment_pct}
                onChange={(e) => setAdmin((s) => ({ ...s, increment_pct: e.target.value }))}
                data-testid="increment-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Effective from</label>
              <input
                type="date" className={inputCls} value={admin.increment_effective_from}
                onChange={(e) => setAdmin((s) => ({ ...s, increment_effective_from: e.target.value }))}
                data-testid="increment-date-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Management remarks</label>
              <input
                className={inputCls} value={admin.admin_comment}
                onChange={(e) => setAdmin((s) => ({ ...s, admin_comment: e.target.value }))}
                data-testid="admin-comment-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            On publish the increment is queued as a salary revision and auto-applies on the
            effective date, so the next payroll run uses the new base salary. Edit or cancel it
            from the Payroll page any time before then.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => approve(true)} disabled={busy === 'approve' || review.status === 'published'}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
              data-testid="approve-publish-btn"
            >
              {busy === 'approve' ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              Approve &amp; publish
            </button>
            {review.status === 'published' && (
              <button
                onClick={reopen} disabled={busy === 'reopen'}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="reopen-btn"
              >
                <Undo2 size={15} /> Reopen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewDetailPage;
